import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useEffect, useMemo, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Button, FormHelperText, FormLabel, Grid, Typography, makeStyles } from "@material-ui/core";
import { CheckboxWithLabelField, CurrencyField, MuiAutocompleteField, TextField } from "@ploy-ui/form-fields";
import { useCallback } from "react";
import { isArray } from "lodash";
import { FormHelperTextContainerWrapper, HelperTextIconButton, LabelComponent } from "../..";
import { defineMessages, useIntl } from "react-intl";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import clsx from "clsx";
import { v4 as uuid } from "uuid";
import { Formik, Field as FormikField, useFormikContext } from "formik";
import { addRegisteredSectionLayout } from "../../section/sectionLayoutDescriptions";
import { fieldOptionListVariableName, useVariableData } from "@ploy-lib/calculation";
import * as Yup from "yup";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import { ErrorDisplay, ApplicationDebtSource } from "@ploy-lib/types";
const DEBTS_FIELD_NAME = "Calculator.Debts";
const useStyles = makeStyles((theme) => ({
    root: {},
    listItem: {
        padding: theme.spacing(2)
    },
    expanded: {
        backgroundColor: theme.palette.primary.light,
        border: `1px solid ${theme.palette.secondary.light}`
    },
    childNumber: {
        paddingBottom: theme.spacing(2),
        paddingTop: theme.spacing(2)
    },
    header: {
        fontWeight: 700,
        paddingBottom: theme.spacing(4),
        padding: theme.spacing(0.5)
    },
    debtInternalLabel: {
        marginBottom: theme.spacing(2),
        paddingLeft: "0px !important" // Override Grid item xs' left padding
    },
    limitedDebtElementContainer: {
        marginTop: theme.spacing(4)
    },
    suppliedDebtsWrapper: {
        border: "1px solid #757575",
        borderRadius: 4,
        backgroundColor: "#fff",
        padding: theme.spacing(2)
    },
    suppliedDebtsElement: {
        marginBottom: theme.spacing(1),
        "&:last-child": {
            marginBottom: 0
        }
    },
    lenderLabel: {
        overflowWrap: "anywhere",
        fontWeight: "bold"
    },
    boldText: {
        fontWeight: "bold",
        fontSize: 16
    },
    addButton: {
        borderRadius: 4,
        justifyContent: "start",
        padding: "8px 24px",
        border: `1px solid ${theme.palette.common.black}`,
        fontSize: 16,
        background: "#fff",
        marginTop: theme.spacing(1)
    },
    addButtonPlusIcon: {
        marginRight: 8
    },
    expandMoreIcon: {
        color: theme.palette.primary.main
    },
    spacingAboveField: {
        paddingTop: 12
    },
    innerHelperTextContainer: {
        paddingLeft: 5
    }
}), { name: "DployLimitedDebtField" });
const messages = defineMessages({
    loanType: {
        id: "template-form.field.limitedDebt.loanType",
        defaultMessage: "Loan type"
    },
    amount: {
        id: "template-form.field.limitedDebt.amount",
        defaultMessage: "Amount"
    },
    delete: {
        id: "template-form.field.limitedDebt.delete",
        defaultMessage: "Delete"
    },
    update: {
        id: "template-form.field.limitedDebt.update",
        defaultMessage: "Update"
    },
    save: {
        id: "template-form.field.limitedDebt.save",
        defaultMessage: "Save"
    },
    rememberToSave: {
        id: "template-form.field.limitedDebt.rememberToSave",
        defaultMessage: "Click the button to save the changes"
    },
    addMore: {
        id: "template-form.field.limitedDebt.addMore",
        defaultMessage: "Add more"
    },
    lender: {
        id: "template-form.field.limitedDebt.lender",
        defaultMessage: "Lender"
    },
    loanNumber: {
        id: "template-form.field.limitedDebt.loanNumber",
        defaultMessage: "Loan number"
    },
    newLoan: {
        id: "template-form.field.limitedDebt.newLoan",
        defaultMessage: "New loan"
    },
    willBeFulfilled: {
        id: "template-form.field.limitedDebt.willBeFulfilled",
        defaultMessage: "Will be fulfilled after sale"
    },
    willBeFulfilledHelperText: {
        id: "template-form.field.limitedDebt.willBeFulfilledHelperText",
        defaultMessage: "Check if the loan will be fulfilled when selling your existing home"
    },
    missingLenderHeader: {
        id: "template-form.field.limitedDebt.missingLenderHeader",
        defaultMessage: "Supply lender"
    },
    loanTypeIsRequired: {
        id: "template-form.field.limitedDebt.loanTypeIsRequired",
        defaultMessage: "Loan type is required"
    },
    loanNumberIsRequired: {
        id: "template-form.field.limitedDebt.loanNumberIsRequired",
        defaultMessage: "Loan number is required"
    },
    lenderIsRequired: {
        id: "template-form.field.limitedDebt.lenderIsRequired",
        defaultMessage: "Lender is required"
    },
    amountIsRequired: {
        id: "template-form.field.limitedDebt.amountIsRequired",
        defaultMessage: "Amount is required and must be larger than zero"
    },
    debtRegistryHelperText: {
        id: "template-form.field.limitedDebt.debtRegistryHelperText",
        defaultMessage: "Retrieved debt"
    }
});
const debtSourceMessages = defineMessages({
    [ApplicationDebtSource.NotSet]: {
        id: "template-form.field.limitedDebt.debtSource.notSet",
        defaultMessage: "Not set"
    },
    [ApplicationDebtSource.Applicant]: {
        id: "template-form.field.limitedDebt.debtSource.applicant",
        defaultMessage: "Applicant"
    },
    [ApplicationDebtSource.DebtRegistry]: {
        id: "template-form.field.limitedDebt.debtSource.debtRegistry",
        defaultMessage: "Debt registry"
    },
    [ApplicationDebtSource.Taxdata]: {
        id: "template-form.field.limitedDebt.debtSource.taxdata",
        defaultMessage: "Tax data"
    },
    [ApplicationDebtSource.CustomerEngagement]: {
        id: "template-form.field.limitedDebt.debtSource.customerEngagement",
        defaultMessage: "Customer engagement" // Override in tenant-specific translations file for correct backoffice label
    }
});
const fieldsToTouch = [
    "loanType",
    "lender",
    "amount",
    "loanNumber"
];
const fieldsToTouchDictionary = Object.fromEntries(fieldsToTouch.map(key => [key, true]));
const LimitedDebtElement = (props) => {
    const classes = useStyles();
    const intl = useIntl();
    const [expanded, setExpanded] = useState(false);
    const { loanTypesDict, loanTypesValues } = props;
    const applicationDebtSchema = useMemo(() => makeApplicationDebtSchema(intl, loanTypesValues), [intl, loanTypesValues]);
    // On initial render, if data doesn't have a loanType: Expand the Accordion
    useEffect(() => {
        if (!props.data.loanType)
            setExpanded(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const { touched, errors, isValid } = useFormikContext();
    const isFieldTouched = !!touched["Calculator"]?.["Debts"];
    const isExpandableAndEditableDebtBox = props.onUpdate !== undefined;
    const { _isNewEntry } = props.data;
    const isExpanded = _isNewEntry || expanded;
    return (_jsx(Formik, { initialValues: props.data, enableReinitialize: true, validationSchema: applicationDebtSchema, onSubmit: (debtToUpdate, helpers) => {
            props.onUpdate?.(debtToUpdate);
            setExpanded(false);
            return Promise.resolve(debtToUpdate);
        }, children: formikProps => (_jsxs(Accordion, { expanded: isExpanded, onChange: async (_, updatedExpanded) => {
                if (!isExpandableAndEditableDebtBox)
                    return;
                const errorMessages = await formikProps.validateForm();
                // validateForm() doesn't set formikProps.isValid => false, or formikProps.isDirty => true..
                // Must instead take a look at validationresult to prevent closing of Accordion
                if (Object.keys(errorMessages).length > 0 || !formikProps.isValid) {
                    // Prevent closing if there are errors
                    formikProps.setTouched(fieldsToTouchDictionary, true);
                    return;
                }
                if (!formikProps.dirty)
                    setExpanded(updatedExpanded);
            }, className: clsx(classes.listItem, {
                [classes.expanded]: isExpanded
            }), children: [_jsx(AccordionSummary, { style: isExpandableAndEditableDebtBox ? undefined : { cursor: "default" }, expandIcon: isExpandableAndEditableDebtBox ? (_jsx(ExpandMoreIcon, { className: classes.expandMoreIcon })) : null, children: isExpanded ? (_jsx(Typography, { className: classes.lenderLabel, children: props.data.lender || intl.formatMessage(messages.newLoan) })) : (_jsxs(Grid, { container: true, justifyContent: "space-between", children: [_jsx(Typography, { className: classes.boldText, children: loanTypesDict[formikProps.values.loanType] ??
                                    formikProps.values.loanType }), _jsx(Typography, { className: classes.boldText, children: intl.formatNumber(formikProps.values.amount, {
                                    format: "currency",
                                    minimumFractionDigits: 0
                                }) })] })) }), _jsx(AccordionDetails, { children: _jsxs(Grid, { container: true, spacing: 4, direction: "column", children: [props.canEditEverything ? (_jsx(Grid, { item: true, children: _jsx(DebtField, { fieldName: "lender", component: TextField, isCalcFieldTouched: isFieldTouched, className: classes.spacingAboveField }) })) : (_jsx(Grid, { item: true, style: { paddingTop: 8 }, children: _jsx(Typography, { variant: "body2", className: classes.lenderLabel, gutterBottom: true, children: props.data.loanNumber }) })), _jsx(Grid, { item: true, children: _jsx(DebtField, { fieldName: "loanType", component: props => (_jsx(MuiAutocompleteField, { ...props, onSelectItem: item => {
                                            props.form.setFieldValue("willBeFulfilled", false, false);
                                        } })), isCalcFieldTouched: isFieldTouched, restProps: {
                                        items: loanTypesValues,
                                        disableClearable: true,
                                        getItemLabel: (lt) => loanTypesDict[lt],
                                        getItemValue: (lt) => loanTypesDict[lt] != null ? lt : undefined
                                    } }) }), _jsx(Grid, { item: true, children: _jsx(DebtField, { fieldName: "amount", component: CurrencyField, isCalcFieldTouched: isFieldTouched, restProps: {
                                        minimumFractionDigits: 0,
                                        emptyEqualsZero: false
                                    } }) }), props.canEditEverything ? (_jsx(Grid, { item: true, children: _jsx(DebtField, { fieldName: "loanNumber", component: TextField, isCalcFieldTouched: isFieldTouched }) })) : null, (props.canEditEverything || props.canEditWillBeFulfilled) &&
                                props.fields?.some(f => f.name === "WillBeFulfilled") &&
                                formikProps.values.loanType === "mortgage" ? (_jsx(Grid, { item: true, children: _jsx(DebtField, { fieldName: "willBeFulfilled", hasHelperText: true, component: CheckboxWithLabelField, isCalcFieldTouched: isFieldTouched }) })) : null, props.onUpdate != null &&
                                (props.canEditAmount ||
                                    props.canEditLoanType ||
                                    props.canEditEverything) ? (_jsxs(Grid, { item: true, container: true, justifyContent: "space-between", children: [_jsx(Button, { disabled: !formikProps.dirty || !formikProps.isValid, onClick: () => {
                                            formikProps.submitForm();
                                        }, variant: "contained", children: _isNewEntry
                                            ? intl.formatMessage(messages.save)
                                            : intl.formatMessage(messages.update) }), props.onDelete != null && props.canEditEverything ? (_jsx(Button, { onClick: () => props.onDelete?.(props.data), variant: "contained", children: intl.formatMessage(messages.delete) })) : null] })) : null, isFieldTouched && !isValid && formikProps.dirty && (_jsx(Grid, { item: true, children: _jsx(FormHelperText, { error: true, children: intl.formatMessage(messages.rememberToSave) }) }))] }) })] })) }));
};
const DebtField = (props) => {
    const { fieldName, component, isCalcFieldTouched, className, restProps, hasHelperText } = props;
    const { validateForm, isValid, setTouched } = useFormikContext();
    const [showHelperText, setShowHelperText] = useState(false);
    const classes = useStyles();
    const intl = useIntl();
    useEffect(() => {
        // This validation is triggered when the user has clicked on the form's "Next-button"
        // Validation on field-input is handled in the parent component.
        const validateAsync = async () => {
            const validationErrors = await validateForm();
            if (Object.entries(validationErrors).length > 0) {
                setTouched(fieldsToTouchDictionary, true); // Touch all debt-fields to display error
            }
        };
        // When user has clicked the form's "Next-button", validate the form:
        if (isCalcFieldTouched) {
            validateAsync();
        }
        // only "isCalcFieldTouched" in deps-array
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCalcFieldTouched]);
    return (_jsxs("div", { className: className, children: [_jsx(FormikField, { name: fieldName, component: component, label: _jsxs(Grid, { container: true, alignItems: "center", children: [_jsx(Grid, { item: true, xs: 11, children: intl.formatMessage(messages[fieldName]) }), hasHelperText ? (_jsx(Grid, { item: true, xs: 1, className: classes.innerHelperTextContainer, children: _jsx(HelperTextIconButton, { toggleHelperText: () => setShowHelperText(display => !display), showHelperText: showHelperText }) })) : null] }), fullWidth: true, error: !isValid, errorDisplay: ErrorDisplay.Touched, ...restProps }), hasHelperText ? (_jsx(Grid, { item: true, container: true, xs: 12, children: showHelperText ? (_jsx(FormHelperTextContainerWrapper, { helperText: intl.formatMessage(messages[`${fieldName}HelperText`]) })) : null })) : null] }));
};
const LimitedDebtsInternal = memo((props) => {
    const intl = useIntl();
    const classes = useStyles();
    const { debtList } = props;
    const loanTypesRaw = useVariableData("Calculator", fieldOptionListVariableName("LoanTypeOptionHelper")).value?.filter(l => l.key !== "CustomerEngagement");
    const [loanTypesDict, loanTypesValues] = useMemo(() => [
        Object.fromEntries((loanTypesRaw ?? []).map(lt => [lt.key, lt.value])),
        (loanTypesRaw ?? []).map(l => l.key)
    ], [loanTypesRaw]);
    return (_jsxs(Grid, { container: true, className: props.className, children: [props.label ? (_jsx(Grid, { item: true, xs: 12, className: classes.debtInternalLabel, children: _jsx(FormLabel, { component: "legend", children: props.label }) })) : null, debtList.length > 0 ? (_jsx(Grid, { container: true, spacing: 1, children: debtList.map(data => (_jsx(Grid, { item: true, xs: 12, children: _jsx(LimitedDebtElement, { data: data, onUpdate: props.onUpdate, onDelete: props.onDelete, canEditEverything: data.source === ApplicationDebtSource.Applicant, canEditAmount: data.source === ApplicationDebtSource.Taxdata, canEditLoanType: data.source === ApplicationDebtSource.Taxdata, canEditWillBeFulfilled: data.source === ApplicationDebtSource.Taxdata, loanTypesDict: loanTypesDict, loanTypesValues: loanTypesValues, fields: props.fields }) }, data._frontendOnlyId ?? data.applicationDebtId))) })) : null, props.onAdd != null ? (_jsx(Grid, { item: true, xs: 12, children: _jsxs(Button, { onClick: props.onAdd, variant: "outlined", className: classes.addButton, fullWidth: true, children: [_jsx(AddCircleOutlineIcon, { className: classes.addButtonPlusIcon }), intl.formatMessage(messages.addMore)] }) })) : null] }));
});
function makeApplicationDebtSchema(intl, loanTypesValues) {
    return Yup.object({
        loanType: Yup.string()
            .required(intl.formatMessage(messages.loanTypeIsRequired))
            .oneOf(loanTypesValues),
        lender: Yup.string()
            .required(intl.formatMessage(messages.lenderIsRequired))
            .min(3, intl.formatMessage(messages.lenderIsRequired))
            .max(50, intl.formatMessage(messages.lenderIsRequired)),
        amount: Yup.number()
            .required(intl.formatMessage(messages.amountIsRequired))
            .typeError(intl.formatMessage(messages.amountIsRequired)) // When input-field is empty (undefined => NaN)
            .min(0, intl.formatMessage(messages.amountIsRequired))
            .max(99999999, intl.formatMessage(messages.amountIsRequired)),
        loanNumber: Yup.string()
            .required(intl.formatMessage(messages.loanNumberIsRequired))
            .matches(/^\d{11}$/, intl.formatMessage(messages.loanNumberIsRequired))
    });
}
export function debtExistencePredicate(itemFromCollection, needle) {
    return ((needle._frontendOnlyId != null &&
        needle._frontendOnlyId === itemFromCollection._frontendOnlyId) ||
        (needle._frontendOnlyId == null &&
            needle.applicationDebtId === itemFromCollection.applicationDebtId));
}
export function ensureCollectionHasIds(items) {
    return (!isArray(items) ? [] : items || []).map(debt => 
    // Just in case application / calc rules are reloaded but debt are not saved to database. May cause rerenders.
    (debt.applicationDebtId == null || debt.applicationDebtId === 0) &&
        !debt._frontendOnlyId
        ? { ...debt, _frontendOnlyId: uuid() }
        : debt);
}
const LimitedDebtsField = memo((props) => {
    const intl = useIntl();
    const classes = useStyles();
    const debtList = useMemo(() => ensureCollectionHasIds(props.field.value), [props.field.value]);
    const maybeUntouchCalcField = useCallback((debts) => {
        if (debts.some(x => x._isNewEntry)) {
            // Any new, unsaved, debts?
            // If so, don't untouch the Debts field (keep it touched)
            return;
        }
        // No new, unsaved entries - untouch the Debts field (enabling the form's Next-button)
        props.form.setFieldTouched(DEBTS_FIELD_NAME, false);
    }, [props.form]);
    const addItem = useCallback(() => {
        const updatedList = [
            ...debtList,
            {
                lender: "",
                amount: NaN,
                loanType: "",
                source: ApplicationDebtSource.Applicant,
                applicationDebtId: undefined,
                loanNumber: "",
                termAmount: 0,
                refinance: false,
                comment: "",
                _frontendOnlyId: uuid(),
                _isNewEntry: true
            }
        ];
        props.form.setFieldValue(props.field.name, updatedList);
    }, [debtList, props.field.name, props.form]);
    const updateItem = useCallback((updatedData) => {
        const updatedList = debtList.map(debt => {
            const entry = debtExistencePredicate(debt, updatedData)
                ? updatedData
                : debt;
            delete entry._isNewEntry;
            return entry;
        });
        props.form.setFieldValue(props.field.name, updatedList);
        maybeUntouchCalcField(updatedList);
    }, [debtList, props.field.name, props.form, maybeUntouchCalcField]);
    const deleteItem = useCallback((debtToDelete) => {
        const updatedList = debtList.filter(debt => !debtExistencePredicate(debt, debtToDelete));
        props.form.setFieldValue(props.field.name, updatedList);
        maybeUntouchCalcField(updatedList);
    }, [debtList, props.field.name, props.form, maybeUntouchCalcField]);
    const userSuppliedDebts = useMemo(() => debtList.filter(debt => debt.source === ApplicationDebtSource.Applicant), [debtList]);
    const debtRegistryDebts = useMemo(() => debtList.filter(debt => debt.source === ApplicationDebtSource.DebtRegistry), [debtList]);
    const taxRegistryDebts = useMemo(() => debtList.filter(debt => debt.source === ApplicationDebtSource.Taxdata), [debtList]);
    const backofficeEngagementDebts = useMemo(() => debtList.filter(debt => debt.source === ApplicationDebtSource.CustomerEngagement), [debtList]);
    const [showDebtRegistryHelperText, setShowHelperText] = useState(false);
    const toggleDebtRegistryHelperText = useCallback((e) => {
        setShowHelperText(show => !show);
        e.stopPropagation();
    }, []);
    return (_jsxs(Grid, { container: true, spacing: 1, className: clsx(classes.root, props.className), onClick: props.onClick, children: [props.label ? (_jsx(Grid, { item: true, xs: 12, children: _jsx(FormLabel, { component: "legend", children: props.label }) })) : null, taxRegistryDebts.length > 0 ? (_jsx(Grid, { item: true, xs: 12, children: _jsx(LimitedDebtsInternal, { fields: props.fields, debtList: taxRegistryDebts, label: intl.formatMessage(debtSourceMessages[ApplicationDebtSource.Taxdata]), onUpdate: updateItem }) })) : null, _jsx(Grid, { item: true, xs: 12, children: _jsx(LimitedDebtsInternal, { fields: props.fields, debtList: userSuppliedDebts, label: "" // Shouldn't have a separate title for applicant, set empty string
                    , onAdd: addItem, onUpdate: updateItem, onDelete: deleteItem }) }), backofficeEngagementDebts.length > 0 ? (_jsx(Grid, { item: true, xs: 12, children: _jsx(LimitedDebtsInternal, { className: classes.limitedDebtElementContainer, fields: props.fields, debtList: [
                        {
                            loanType: "Lån", // They all have the same loanType
                            amount: backofficeEngagementDebts.reduce((acc, curr) => {
                                return acc + curr.amount;
                            }, 0),
                            lender: backofficeEngagementDebts[0].lender,
                            source: ApplicationDebtSource.CustomerEngagement,
                            _frontendOnlyId: backofficeEngagementDebts[0]._frontendOnlyId,
                            applicationDebtId: backofficeEngagementDebts[0].applicationDebtId,
                            comment: "",
                            loanNumber: "",
                            originalAmount: 0,
                            refinance: false,
                            refinanceAccount: "",
                            refinanceAmount: 0,
                            termAmount: 0,
                            willBeFulfilled: false
                        }
                    ], label: intl.formatMessage(debtSourceMessages[ApplicationDebtSource.CustomerEngagement]) }) })) : null, debtRegistryDebts.length > 0 ? (_jsx(Grid, { item: true, xs: 12, children: _jsx(LimitedDebtsInternal, { fields: props.fields, className: classes.limitedDebtElementContainer, debtList: debtRegistryDebts, label: _jsx(LabelComponent, { labelText: intl.formatMessage(debtSourceMessages[ApplicationDebtSource.DebtRegistry]), isFirst: true, trimmedHelperText: intl.formatMessage(messages.debtRegistryHelperText), displayHelperTextAboveField: true, variant: "outlined", showHelperText: showDebtRegistryHelperText, toggleHelperText: toggleDebtRegistryHelperText }) }) })) : null] }));
});
export const LimitedDebtsSection = (sectionProps) => {
    // This section started as a custom field 😅
    return (_jsx(FormikField, { component: LimitedDebtsField, name: DEBTS_FIELD_NAME, className: sectionProps.className, onClick: sectionProps.onClick, literal: sectionProps.literal, fields: sectionProps.fields }));
};
// Credit: https://github.com/jaredpalmer/formik/issues/2769#issuecomment-1872382784
// export const FormikAutoSubmit = () => {
// 	/*
// 	  This component is used to automatically submit the form when the form is valid
// 	  and has been changed(dirty).
// 	 */
// 	const { isValid, values, dirty, submitForm } = useFormikContext();
// 	useEffect(() => {
// 		if (isValid && dirty) {
// 			submitForm();
// 			return;
// 		}
// 	}, [isValid, values, dirty, submitForm]);
// 	return null;
// };
addRegisteredSectionLayout({
    displayName: "LimitedDebtsSection",
    name: "LimitedDebtsSection",
    settings: {}
});
LimitedDebtsField.displayName = "LimitedDebtsField";
LimitedDebtsSection.displayName = "LimitedDebtsSection";
//# sourceMappingURL=LimitedDebtField.js.map